/**
 * @file Segmented 分段控制器
 * @description 紧凑互斥选项组（单尺寸）。
 *
 * @whenToUse 视图切换、短枚举筛选（日/周/月）。
 * @whenNotToUse 多页签内容区（用 Tabs）；侧栏导航。
 */

import type { ReactNode } from 'react'
import { cn } from '@/lib/cn.ts'

export type SegmentedOption = {
  value: string
  label: ReactNode
  disabled?: boolean
  className?: string
  activeClassName?: string
}

export type SegmentedProps = {
  options: SegmentedOption[]
  value: string
  onChange: (value: string) => void
  className?: string
  optionClassName?: string
  /** 无障碍标签 */
  'aria-label'?: string
}

export function Segmented({
  options,
  value,
  onChange,
  className,
  optionClassName,
  'aria-label': ariaLabel = '分段选项',
}: SegmentedProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex h-8 items-center gap-1 rounded-md border border-border bg-raised p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={option.disabled}
            className={cn(
              'inline-flex h-full items-center rounded-md px-2 text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
              active
                ? 'border border-border bg-primary/8 font-medium text-primary'
                : 'border border-transparent text-muted hover:opacity-90',
              option.className,
              option.activeClassName,
              optionClassName,
            )}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
